/**
 * Utilitários para validação e reconstrução de referências de Image
 * Solução para problemas de perda de referência após troca de aba ou tempo
 */

/**
 * Valida se uma referência de Image é válida e completa
 * @param {HTMLImageElement} img - Objeto Image a ser validado
 * @returns {boolean} - true se a imagem é válida e completa
 */
export function isValidImage(img) {
  if (!img) return false;
  if (!(img instanceof HTMLImageElement)) return false;
  if (!img.complete) return false;
  if (img.naturalWidth === 0 || img.naturalHeight === 0) return false;
  return true;
}

/**
 * Aguarda o carregamento completo de uma imagem
 * @param {HTMLImageElement} img - Objeto Image a aguardar
 * @param {number} timeout - Timeout em ms (padrão: 5000)
 * @returns {Promise<HTMLImageElement>} - Promise que resolve quando a imagem está completa
 */
export function waitForImageLoad(img, timeout = 5000) {
  return new Promise((resolve, reject) => {
    if (!img) {
      reject(new Error('Image is null'));
      return;
    }

    if (!(img instanceof HTMLImageElement)) {
      reject(new Error('Not an HTMLImageElement'));
      return;
    }

    // Se já está completo, resolver imediatamente
    if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
      resolve(img);
      return;
    }

    // Configurar timeout
    const timeoutId = setTimeout(() => {
      reject(new Error('Image load timeout'));
    }, timeout);

    // Configurar listeners
    const onLoad = () => {
      clearTimeout(timeoutId);
      img.removeEventListener('load', onLoad);
      img.removeEventListener('error', onError);
      resolve(img);
    };

    const onError = () => {
      clearTimeout(timeoutId);
      img.removeEventListener('load', onLoad);
      img.removeEventListener('error', onError);
      reject(new Error('Image load error'));
    };

    img.addEventListener('load', onLoad);
    img.addEventListener('error', onError);
  });
}

/**
 * Reconstrói uma referência de Image a partir de um src
 * @param {string} src - URL ou data URL da imagem
 * @param {number} timeout - Timeout em ms (padrão: 10000)
 * @returns {Promise<HTMLImageElement>} - Promise que resolve com nova instância de Image
 */
export function rebuildImageFromSrc(src, timeout = 10000) {
  return new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('Source is required'));
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';

    const timeoutId = setTimeout(() => {
      reject(new Error('Image rebuild timeout'));
    }, timeout);

    img.onload = () => {
      clearTimeout(timeoutId);
      resolve(img);
    };

    img.onerror = () => {
      clearTimeout(timeoutId);
      reject(new Error('Image rebuild error'));
    };

    img.src = src;
  });
}

/**
 * Valida e reconstrói uma referência de Image se necessário
 * @param {Object} imageData - Objeto com dados da imagem ({image, src, originalSrc})
 * @param {Object} options - Opções {timeout: number, preferOriginalSrc: boolean}
 * @returns {Promise<HTMLImageElement|null>} - Promise que resolve com Image válido ou null
 */
export async function validateAndRebuildImage(imageData, options = {}) {
  const { timeout = 10000, preferOriginalSrc = false } = options;

  if (!imageData) {
    console.warn('[validateAndRebuildImage] imageData is null');
    return null;
  }

  const img = imageData.image;

  // Se não há referência de image, tentar reconstruir
  if (!img) {
    const srcToUse = preferOriginalSrc 
      ? (imageData.originalSrc || imageData.src)
      : (imageData.src || imageData.originalSrc);

    if (!srcToUse) {
      console.error('[validateAndRebuildImage] No image reference and no src available');
      return null;
    }

    console.warn('[validateAndRebuildImage] No image reference, rebuilding from src');
    try {
      return await rebuildImageFromSrc(srcToUse, timeout);
    } catch (error) {
      console.error('[validateAndRebuildImage] Failed to rebuild:', error);
      return null;
    }
  }

  // Verificar se é instância válida
  if (!(img instanceof HTMLImageElement)) {
    console.warn('[validateAndRebuildImage] Image is not HTMLImageElement, rebuilding');
    const srcToUse = preferOriginalSrc 
      ? (imageData.originalSrc || imageData.src)
      : (imageData.src || imageData.originalSrc);

    if (!srcToUse) {
      console.error('[validateAndRebuildImage] Invalid image type and no src available');
      return null;
    }

    try {
      return await rebuildImageFromSrc(srcToUse, timeout);
    } catch (error) {
      console.error('[validateAndRebuildImage] Failed to rebuild:', error);
      return null;
    }
  }

  // Verificar se está completo
  if (!img.complete) {
    console.warn('[validateAndRebuildImage] Image not complete, waiting for load');
    try {
      await waitForImageLoad(img, timeout);
      return img;
    } catch (error) {
      console.error('[validateAndRebuildImage] Image load failed:', error);
      // Tentar reconstruir
      const srcToUse = imageData.src || imageData.originalSrc;
      if (srcToUse) {
        try {
          return await rebuildImageFromSrc(srcToUse, timeout);
        } catch (rebuildError) {
          console.error('[validateAndRebuildImage] Rebuild failed:', rebuildError);
          return null;
        }
      }
      return null;
    }
  }

  // Verificar dimensões
  if (img.naturalWidth === 0 || img.naturalHeight === 0) {
    console.warn('[validateAndRebuildImage] Image has invalid dimensions, rebuilding');
    const srcToUse = imageData.src || imageData.originalSrc;
    if (srcToUse) {
      try {
        return await rebuildImageFromSrc(srcToUse, timeout);
      } catch (error) {
        console.error('[validateAndRebuildImage] Rebuild failed:', error);
        return null;
      }
    }
    return null;
  }

  // Image é válido
  return img;
}

/**
 * Valida múltiplas imagens em um array
 * @param {Array} imagesArray - Array de objetos com dados de imagem
 * @returns {Promise<Array>} - Array de resultados de validação
 */
export async function validateMultipleImages(imagesArray) {
  const results = await Promise.allSettled(
    imagesArray.map(img => validateAndRebuildImage(img))
  );
  
  return results.map((result, index) => ({
    index,
    success: result.status === 'fulfilled',
    image: result.status === 'fulfilled' ? result.value : null,
    error: result.status === 'rejected' ? result.reason : null
  }));
}

